import { XrdlgResponseData, XrinvResponseData, XritmResponseData } from '../../types/comms-check';
import { buildXrdlgRequest, buildXrinvRequest, buildXritmRequest, getAddress } from '../util';
import { getXrdlgData, getXrinvData, getXritmData } from './util';
import { atomProxyCommsCheckClient } from '../../api/atomProxyClient';
import { buildPostRequest } from '../../api/apiClient';

type AtomCheckErrorState = 'PROXY_L1_ERROR' | 'UNEXPECTED_L1_ERROR' | 'PROXY_L2_ERROR' | 'UNEXPECTED_L2_ERROR' | 'FAIL';

type AtomCheckOutcome =
  | { state: AtomCheckErrorState | 'CANCELLED'; l2Response: null }
  | { state: 'SUCCESS'; l2Response: any }
  | { state: 'PROCESSING'; l2Response: any | null };

interface AtomCheckConfig {
  generateXml: (request: any) => Promise<Response>;
  getXml: (request: any) => Promise<Response>;
  request: any;
  initialTimeoutMs: number;
  pollTimeoutMs: number;
  maxRetries: number;
  signal?: AbortSignal;
}

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Runs one atom proxy check end-to-end: L1 generate-xml, an initial wait, then L2 get-xml
 * polling until a final state or until the retry budget is exhausted (-> PROCESSING).
 * Pure request/response logic: no React state, no context, no reporting. Never rejects.
 */
async function executeAtomCheck(config: AtomCheckConfig): Promise<AtomCheckOutcome> {
  if (config.signal?.aborted) {
    return { state: 'CANCELLED', l2Response: null };
  }

  let l1Response;
  try {
    const httpResponse = await config.generateXml(config.request);
    if (httpResponse.status === 500) {
      return { state: 'PROXY_L1_ERROR', l2Response: null };
    }
    l1Response = await httpResponse.json();
  } catch {
    return { state: 'UNEXPECTED_L1_ERROR', l2Response: null };
  }

  await delay(config.initialTimeoutMs);

  let lastProcessingResponse: any = null;
  for (let attempt = 0; attempt <= config.maxRetries; attempt += 1) {
    if (attempt > 0) {
      await delay(config.pollTimeoutMs);
    }
    if (config.signal?.aborted) {
      return { state: 'CANCELLED', l2Response: null };
    }
    try {
      const httpResponse = await config.getXml(buildPostRequest(l1Response.atomL1response));
      if (httpResponse.status === 500) {
        if (attempt === config.maxRetries) {
          return { state: 'PROXY_L2_ERROR', l2Response: null };
        }
        continue;
      }
      const l2Response = await httpResponse.json();
      if (l2Response.bolQueryState === 'FAIL') {
        return { state: 'FAIL', l2Response: null };
      }
      if (l2Response.bolQueryState === 'SUCCESS') {
        return { state: 'SUCCESS', l2Response };
      }
      // PROCESSING (or anything unrecognised) counts against the retry budget
      lastProcessingResponse = l2Response;
    } catch {
      return { state: 'UNEXPECTED_L2_ERROR', l2Response: null };
    }
  }
  return { state: 'PROCESSING', l2Response: lastProcessingResponse };
}

/**
 * Reading check for a single (mpxn, msn) pair — fuel independent: call with
 * (MPAN, elec MSN) for elec and (MPRN, gas MSN) for gas. XRITM polls on its own
 * extended budget: 5 minutes 15 seconds in total (15s initial + 10 x 30s in prd/uat).
 */
export async function runXritmCheck(mpxn: string, msn: string, signal?: AbortSignal): Promise<XritmResponseData> {
  const outcome = await executeAtomCheck({
    generateXml: atomProxyCommsCheckClient.generateXmlXritm,
    getXml: atomProxyCommsCheckClient.getXmlXritm,
    request: buildXritmRequest(mpxn, msn),
    initialTimeoutMs: parseInt(`${process.env.REACT_APP_COMMS_CHECK_XRITM_GET_XML_INITIAL_TIMEOUT}`, 10),
    pollTimeoutMs: parseInt(`${process.env.REACT_APP_COMMS_CHECK_GET_XML_TIMEOUT}`, 10),
    maxRetries: parseInt(`${process.env.REACT_APP_COMMS_CHECK_XRITM_GET_XML_MAX_RETRIES}`, 10),
    signal,
  });

  try {
    if (outcome.state === 'SUCCESS') {
      return {
        state: 'SUCCESS',
        data: getXritmData(outcome.l2Response) || [],
        msn: outcome.l2Response.atomL2response['0']?.xml_string?.Body?.XRITM_OUT_02?.MSN,
      };
    }
    if (outcome.state === 'PROCESSING') {
      return {
        state: 'PROCESSING',
        data: (outcome.l2Response && getXritmData(outcome.l2Response)) || [],
        msn: msn,
      };
    }
    if (outcome.state === 'FAIL') {
      return { state: 'FAIL', data: [], msn: msn };
    }
    return { state: outcome.state, data: [], msn: '' };
  } catch {
    return { state: 'UNEXPECTED_L2_ERROR', data: [], msn: '' };
  }
}

/** Inventory check for one MPxN. On SUCCESS the caller extracts the CHF guid for the XRDLG check. */
export async function runXrinvCheck(mpxn: string, signal?: AbortSignal): Promise<XrinvResponseData> {
  const outcome = await executeAtomCheck({
    generateXml: atomProxyCommsCheckClient.generateXmlXrinv,
    getXml: atomProxyCommsCheckClient.getXmlXrinv,
    request: buildXrinvRequest(mpxn),
    initialTimeoutMs: parseInt(`${process.env.REACT_APP_COMMS_CHECK_GET_XML_INITIAL_TIMEOUT}`, 10),
    pollTimeoutMs: parseInt(`${process.env.REACT_APP_COMMS_CHECK_GET_XML_TIMEOUT}`, 10),
    maxRetries: parseInt(`${process.env.REACT_APP_COMMS_CHECK_GET_XML_MAX_RETRIES}`, 10),
    signal,
  });

  try {
    if (outcome.state === 'SUCCESS') {
      return {
        state: 'SUCCESS',
        address: getAddress(outcome.l2Response),
        data: getXrinvData(outcome.l2Response) || [],
      };
    }
    if (outcome.state === 'PROCESSING') {
      return {
        state: 'PROCESSING',
        address: null,
        data: (outcome.l2Response && getXrinvData(outcome.l2Response)) || [],
      };
    }
    return { state: outcome.state, address: null, data: [] };
  } catch {
    return { state: 'UNEXPECTED_L2_ERROR', address: null, data: [] };
  }
}

/** Device log check for one MPxN + CHF guid. Returns ALL log entries; the caller filters/merges. */
export async function runXrdlgCheck(mpxn: string, chfGuid: string | undefined, signal?: AbortSignal): Promise<XrdlgResponseData> {
  const outcome = await executeAtomCheck({
    generateXml: atomProxyCommsCheckClient.generateXmlXrdlg,
    getXml: atomProxyCommsCheckClient.getXmlXrdlg,
    request: buildXrdlgRequest(mpxn, chfGuid),
    initialTimeoutMs: parseInt(`${process.env.REACT_APP_COMMS_CHECK_GET_XML_INITIAL_TIMEOUT}`, 10),
    pollTimeoutMs: parseInt(`${process.env.REACT_APP_COMMS_CHECK_GET_XML_TIMEOUT}`, 10),
    maxRetries: parseInt(`${process.env.REACT_APP_COMMS_CHECK_GET_XML_MAX_RETRIES}`, 10),
    signal,
  });

  try {
    if (outcome.state === 'SUCCESS') {
      return { state: 'SUCCESS', data: getXrdlgData(outcome.l2Response) };
    }
    return { state: outcome.state, data: [] };
  } catch {
    return { state: 'UNEXPECTED_L2_ERROR', data: [] };
  }
}
